/**
 * ReelModelColumn Component
 * Feature 017: Instagram Reel Creation
 * Single model view with reel 5-section structure
 */

import { memo } from 'react';
import { AlertCircle, CheckCircle, Film } from 'lucide-react';
import type { IReelModelResponse, ModelKey } from '../../@types/reels';
import { Button } from '../ui/button';

interface IReelModelColumnProps {
  modelKey: ModelKey;
  modelName: string;
  reelData: IReelModelResponse | undefined;
  status: 'idle' | 'loading' | 'completed' | 'error';
  selectedVariantModel: ModelKey | null;
  isSelecting: boolean;
  onSelectBestVariant: (modelKey: ModelKey) => void;
}

const MODEL_COLORS = {
  claude: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    badge: 'bg-purple-100',
  },
  gemini: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-100',
  },
  openai: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    badge: 'bg-green-100',
  },
};

interface IReelSections {
  Hook?: string;
  Scene_1?: string;
  Scene_2?: string;
  Scene_3?: string;
  CTA?: string;
  Visual_Notes?: string;
  Audio_Notes?: string;
  Hashtags?: string[];
}

/**
 * Parse reel content - handles both clean JSON and markdown-wrapped JSON
 */
function parseReelContent(content: string): IReelSections | null {
  if (!content) return null;

  let jsonText = content.trim();

  // Try direct JSON parse first (clean format)
  try {
    return JSON.parse(jsonText);
  } catch {
    // Not clean JSON, try extracting from markdown
  }

  // Remove markdown code blocks if present
  if (jsonText.startsWith('```')) {
    const lines = jsonText.split('\n');
    // Find the actual JSON content
    const startIdx = lines.findIndex(line => line.trim().startsWith('{'));
    const endIdx =
      lines.length - 1 - [...lines].reverse().findIndex(line => line.trim().endsWith('}'));
    if (startIdx !== -1 && endIdx >= startIdx) {
      jsonText = lines.slice(startIdx, endIdx + 1).join('\n');
    } else {
      // Fallback: remove first and last lines (```json and ```)
      jsonText = lines.slice(1, -1).join('\n').trim();
    }
  }

  // Extract JSON object boundaries
  const jsonStart = jsonText.indexOf('{');
  const jsonEnd = jsonText.lastIndexOf('}');

  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
    jsonText = jsonText.substring(jsonStart, jsonEnd + 1);
  }

  // Try parsing again
  try {
    return JSON.parse(jsonText);
  } catch {
    return null;
  }
}

/**
 * Strips bracketed visual descriptions from script text for cleaner display
 * Example: "Text here [visual note] more text" -> "Text here more text"
 */
function stripBracketedDescriptions(text: string): string {
  if (!text) return text;
  return text.replace(/\s*\[[^\]]+\]/g, '').trim();
}

const ReelModelColumn = ({
  modelKey,
  modelName,
  reelData,
  status,
  selectedVariantModel,
  isSelecting,
  onSelectBestVariant,
}: IReelModelColumnProps) => {
  const colors = MODEL_COLORS[modelKey];
  const isSelected = selectedVariantModel === modelKey;
  const canSelect = status === 'completed' && !reelData?.error;

  // Parse sections from JSON content (handles both clean JSON and markdown-wrapped)
  const sections: IReelSections | null = reelData?.content
    ? parseReelContent(reelData.content)
    : null;

  // Section labels for display
  const sectionLabels = [
    { key: 'Hook', label: 'Hook (0-3s)', icon: '🎬' },
    { key: 'Scene_1', label: 'Scene 1 (3-10s)', icon: '1️⃣' },
    { key: 'Scene_2', label: 'Scene 2 (10-17s)', icon: '2️⃣' },
    { key: 'Scene_3', label: 'Scene 3 (17-24s)', icon: '3️⃣' },
    { key: 'CTA', label: 'CTA (24-30s)', icon: '📢' },
  ];

  return (
    <div
      className={`rounded-lg border ${colors.border} ${colors.bg} p-6 ${isSelected ? 'ring-2 ring-green-500' : ''}`}
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className={`h-5 w-5 ${colors.text}`} />
          <h3 className={`text-lg font-semibold ${colors.text}`}>{modelName}</h3>
        </div>
        <div className="flex items-center gap-2">
          {status === 'completed' && !reelData?.error && (
            <CheckCircle className="h-5 w-5 text-green-600" />
          )}
          {status === 'loading' && (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          )}
          {reelData?.cached && (
            <span
              className={`rounded-full ${colors.badge} px-2 py-1 text-xs font-medium ${colors.text}`}
            >
              Cached
            </span>
          )}
          {isSelected && (
            <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
              Selected
            </span>
          )}
        </div>
      </div>

      {/* Error State */}
      {(status === 'error' || reelData?.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
            <div>
              <h4 className="font-medium text-red-900">Generation Failed</h4>
              <p className="mt-1 text-sm text-red-700">
                {reelData?.error || 'An error occurred during generation'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {status === 'loading' && (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(skeleton => (
            <div key={`skeleton-section-${skeleton}`} className="space-y-2">
              <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
              <div className="h-16 animate-pulse rounded bg-white" />
            </div>
          ))}
        </div>
      )}

      {/* Content - Reel Sections */}
      {status === 'completed' && sections && !reelData?.error && (
        <div className="space-y-4">
          {/* Main Sections */}
          {sectionLabels.map(({ key, label, icon }) => {
            const content = sections?.[key as keyof IReelSections];
            if (!content || typeof content !== 'string') return null;

            return (
              <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
                <h4 className="mb-2 text-sm font-medium text-gray-600">
                  {icon} {label}
                </h4>
                <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                  {stripBracketedDescriptions(content)}
                </p>
              </div>
            );
          })}

          {/* Hashtags */}
          {sections.Hashtags && sections.Hashtags.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="mb-2 text-sm font-medium text-gray-600"># Hashtags</h4>
              <div className="flex flex-wrap gap-2">
                {sections.Hashtags.map(tag => (
                  <span
                    key={tag}
                    className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                  >
                    #{tag.replace(/^#/, '')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Use This Version Button */}
          <div className="mt-6">
            <Button
              onClick={() => onSelectBestVariant(modelKey)}
              disabled={!canSelect || isSelecting}
              className="w-full"
              variant={isSelected ? 'outline' : 'default'}
            >
              {isSelecting && selectedVariantModel === modelKey
                ? 'Saving...'
                : isSelected
                  ? '✓ Selected - Click to Re-select'
                  : 'Use This Version'}
            </Button>
          </div>
        </div>
      )}

      {/* Idle State */}
      {status === 'idle' && (
        <div className="text-center text-sm text-gray-500">Waiting to start...</div>
      )}
    </div>
  );
};

export const ReelModelColumnMemo = memo(ReelModelColumn);
